/**
 * Memory-browser hooks: per-agent topics, tags, the filtered/paginated entity
 * list, and single-entity reads, plus the rename/delete mutations.
 *
 * Reads need only membership; the rename/delete mutations 403 Hub-side for
 * non-operators, so the page hides them rather than relying on the rejection.
 * Each mutation invalidates the whole memory subtree so renamed/deleted rows
 * and recomputed counts render immediately.
 */

import { countMemoryEntries, hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

/**
 * The first page uses the Memory tab's exact key and request, so the Overview
 * and Memory tab share cached data while later cursor pages complete the count.
 */
export function useMemoryEntryCountQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: hubQueryKeys.memoryEntryCount(agentId),
    queryFn: async () => {
      const firstPage = await queryClient.fetchQuery({
        queryKey: hubQueryKeys.memoryEntities(agentId),
        queryFn: () => api.memory.list(agentId),
      });
      return countMemoryEntries(firstPage, (cursor, limit) =>
        api.memory.list(agentId, { cursor, limit }),
      );
    },
    enabled: options.enabled ?? true,
  });
}

export function useMemoryTopicsQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.memoryTopics(agentId),
    queryFn: () => api.memory.topics.list(agentId),
    enabled: options.enabled ?? true,
  });
}

export function useMemoryTagsQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.memoryTags(agentId),
    queryFn: () => api.memory.tags.list(agentId),
    enabled: options.enabled ?? true,
  });
}

export interface MemoryEntityFilter {
  topic?: string;
  tag?: string;
  cursor?: string;
}

export function useMemoryEntitiesQuery(
  agentId: string,
  filter: MemoryEntityFilter,
  options: AgentQueryOptions = {},
) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.memoryEntities(agentId, filter),
    queryFn: () =>
      api.memory.list(agentId, {
        ...(filter.topic ? { topic: filter.topic } : {}),
        ...(filter.tag ? { tag: filter.tag } : {}),
        ...(filter.cursor ? { cursor: filter.cursor } : {}),
      }),
    enabled: options.enabled ?? true,
    // Keep the previous page on screen while the next cursor loads so the
    // list does not flash empty between pages.
    placeholderData: keepPreviousData,
  });
}

export function useMemoryEntityQuery(
  agentId: string,
  handle: string | null,
  options: AgentQueryOptions = {},
) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.memoryEntity(agentId, handle ?? ""),
    queryFn: () => api.memory.read(agentId, handle as string),
    enabled: (options.enabled ?? true) && Boolean(handle),
  });
}

function invalidateMemoryQueries(queryClient: QueryClient, agentId: string) {
  // Every memory list/catalog key descends from the memory-root prefix;
  // invalidate the whole subtree so topic/tag counts and the active filtered
  // page all refetch after a mutation.
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.memoryRoot(agentId) });
}

export function useDeleteMemoryEntityMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (handle: string) => api.memory.delete(agentId, handle),
    onSuccess: () => invalidateMemoryQueries(queryClient, agentId),
  });
}

export function useDeleteMemoryTopicMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.memory.topics.delete(agentId, id),
    onSuccess: () => invalidateMemoryQueries(queryClient, agentId),
  });
}

export function useDeleteMemoryTagMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.memory.tags.delete(agentId, name),
    onSuccess: () => invalidateMemoryQueries(queryClient, agentId),
  });
}

export function useRenameMemoryTopicMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, newId }: { id: string; newId: string }) =>
      api.memory.topics.rename(agentId, id, newId),
    onSuccess: () => invalidateMemoryQueries(queryClient, agentId),
  });
}

export function useRenameMemoryTagMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, newName }: { name: string; newName: string }) =>
      api.memory.tags.rename(agentId, name, newName),
    onSuccess: () => invalidateMemoryQueries(queryClient, agentId),
  });
}
